import React, { useMemo, useState } from 'react';
import { Eye, PencilSimple, ChatCircle, Camera, Plus, Trash, X, CaretRight, CaretDown, FolderSimple, Newspaper, CalendarBlank, GridFour, Rows, Image as ImageIcon, CaretLeft as ChevLeft, CaretRight as ChevRight } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------------
// Grouping helpers
// -----------------------------------------------------------------------------
const _stripProjectPrefix = (folder, projectName) => {
  const f = (folder || '').trim();
  if (!f) return '';
  if (f === projectName) return '';
  if (projectName && f.startsWith(`${projectName}/`)) return f.slice(projectName.length + 1);
  return f;
};

// Try to extract an ISO date (YYYY-MM-DD) from a folder name like "Fil d'actu - 2026-05-15"
const _extractDate = (label) => {
  const m = label.match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};

const _isFeedFolder = (label) => /^fil d'actu/i.test(label || '');

const _formatFolderLabel = (label) => {
  if (!label) return 'Photos generales';
  const d = _extractDate(label);
  if (_isFeedFolder(label) && d) {
    const [y, m, day] = d.split('-');
    return `Fil d'actu - ${day}/${m}/${y}`;
  }
  return label;
};

const groupPhotosByFolder = (photos, projectName) => {
  const groups = new Map();
  for (const p of photos || []) {
    const sub = _stripProjectPrefix(p.folder, projectName);
    const key = sub || '__root__';
    if (!groups.has(key)) groups.set(key, { key, rawLabel: sub, photos: [] });
    groups.get(key).photos.push(p);
  }
  // Sort each group by created_at desc
  for (const g of groups.values()) {
    g.photos.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }
  // Build ordered array: __root__ first, then by date desc (feed folders), then alpha
  const items = Array.from(groups.values());
  items.sort((a, b) => {
    if (a.key === '__root__') return -1;
    if (b.key === '__root__') return 1;
    const da = _extractDate(a.rawLabel);
    const db = _extractDate(b.rawLabel);
    if (da && db) return db.localeCompare(da);
    if (da) return -1;
    if (db) return 1;
    return a.rawLabel.localeCompare(b.rawLabel, 'fr');
  });
  return items;
};

// -----------------------------------------------------------------------------
// PhotoCard (single photo tile)
// -----------------------------------------------------------------------------
const PhotoCard = ({ photo, onUpdateComment, onDelete, editingPhotoId, setEditingPhotoId, editPhotoComment, setEditPhotoComment, setPreviewPhoto, compact = false, readOnly = false }) => (
  <div className="card" style={{ overflow: 'hidden', minWidth: compact ? 220 : 0, scrollSnapAlign: compact ? 'start' : 'none' }} data-testid={`photo-card-${photo.id}`}>
    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setPreviewPhoto(photo)}>
      <img
        src={`${API_URL}${photo.url}`}
        alt={photo.original_filename}
        style={{ width: '100%', height: compact ? 150 : 200, objectFit: 'cover', display: 'block' }}
        onError={(e) => { e.target.style.display = 'none'; }}
        loading="lazy"
      />
      <div style={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Eye size={12} /> Agrandir
      </div>
    </div>
    <div style={{ padding: compact ? 8 : 12 }}>
      {editingPhotoId === photo.id && !readOnly ? (
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input
            type="text"
            className="form-input"
            value={editPhotoComment}
            onChange={(e) => setEditPhotoComment(e.target.value)}
            style={{ flex: 1, fontSize: '0.82rem' }}
            autoFocus
          />
          <button className="btn btn-primary btn-sm" onClick={() => onUpdateComment(photo.id)}>OK</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditingPhotoId(null)}><X size={12} /></button>
        </div>
      ) : (
        <div style={{ marginBottom: 6, minHeight: 18 }}>
          {photo.comment ? (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#0A0B10', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: compact ? 'nowrap' : 'normal' }}>
              <ChatCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {photo.comment}
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#9E9E9E', fontStyle: 'italic' }}>—</p>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem', color: '#5E5F66', gap: 4 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.uploaded_by_name} · {(photo.created_at || '').slice(0, 10)}</span>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={(e) => { e.stopPropagation(); setEditingPhotoId(photo.id); setEditPhotoComment(photo.comment || ''); }}
              data-testid={`photo-edit-${photo.id}`}
              style={{ padding: '2px 6px' }}
            ><PencilSimple size={12} /></button>
            <button
              className="btn btn-danger btn-sm"
              onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
              data-testid={`photo-delete-${photo.id}`}
              style={{ padding: '2px 6px' }}
            ><Trash size={12} /></button>
          </div>
        )}
      </div>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// FolderAccordion (one group of photos)
// -----------------------------------------------------------------------------
const FolderAccordion = ({ group, defaultOpen, viewMode, ...photoProps }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(false); // "Voir toutes" -> grid

  const isRoot = group.key === '__root__';
  const isFeed = !isRoot && _isFeedFolder(group.rawLabel);
  const label = _formatFolderLabel(group.rawLabel);
  const IconComp = isRoot ? FolderSimple : (isFeed ? Newspaper : (_extractDate(group.rawLabel) ? CalendarBlank : FolderSimple));
  const headerColor = isFeed ? '#1D4ED8' : (isRoot ? '#5E5F66' : '#92400E');
  const headerBg = isFeed ? '#EFF6FF' : (isRoot ? '#F4F5F7' : '#FEF3C7');
  const headerBorder = isFeed ? '#BFDBFE' : (isRoot ? '#E2E2E6' : '#FDE68A');

  const photos = group.photos;
  const showCarousel = viewMode === 'carousel' && !expanded && photos.length > 4;

  return (
    <div data-testid={`photo-folder-${group.key}`} style={{ marginBottom: 14, border: `1px solid ${headerBorder}`, borderRadius: 10, overflow: 'hidden', background: 'white' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`photo-folder-toggle-${group.key}`}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: headerBg, border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        {open ? <CaretDown size={14} color={headerColor} weight="bold" /> : <CaretRight size={14} color={headerColor} weight="bold" />}
        <IconComp size={18} color={headerColor} weight="duotone" />
        <span style={{ fontWeight: 700, color: headerColor, fontSize: '0.92rem' }}>{label}</span>
        <span style={{ marginLeft: 'auto', background: 'white', color: headerColor, padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, border: `1px solid ${headerBorder}` }}>
          {photos.length} photo{photos.length > 1 ? 's' : ''}
        </span>
      </button>
      {open && (
        <div style={{ padding: 12 }}>
          {showCarousel ? (
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 6 }}>
              {photos.map(p => (
                <PhotoCard key={p.id} photo={p} compact {...photoProps} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {photos.map(p => (
                <PhotoCard key={p.id} photo={p} {...photoProps} />
              ))}
            </div>
          )}
          {viewMode === 'carousel' && photos.length > 4 && (
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setExpanded(e => !e)}
                data-testid={`photo-folder-expand-${group.key}`}
              >
                {expanded ? 'Reduire' : `Voir toutes (${photos.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// PhotosTabGrouped (main export)
// -----------------------------------------------------------------------------
export const PhotosTabGrouped = ({ photos = [], project = {}, onUpload, onUpdateComment, onDelete, editingPhotoId, setEditingPhotoId, editPhotoComment, setEditPhotoComment, setPreviewPhoto, readOnly = false }) => {
  // 'carousel' = horizontal scroll per folder, 'grid' = full grid per folder, 'flat' = legacy flat grid
  const [viewMode, setViewMode] = useState('carousel');
  const [search, setSearch] = useState('');

  const groups = useMemo(() => groupPhotosByFolder(photos, project?.name || ''), [photos, project?.name]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        photos: g.photos.filter(p =>
          (p.comment || '').toLowerCase().includes(q) ||
          (p.uploaded_by_name || '').toLowerCase().includes(q) ||
          (_formatFolderLabel(g.rawLabel)).toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.photos.length > 0);
  }, [groups, search]);

  const allPhotosFlat = useMemo(
    () => [...photos].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [photos]
  );

  const photoProps = { onUpdateComment, onDelete, editingPhotoId, setEditingPhotoId, editPhotoComment, setEditPhotoComment, setPreviewPhoto, readOnly };

  if (!photos.length) {
    return (
      <div className="card">
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#5E5F66' }}>
          <Camera size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <p>Aucune photo pour ce chantier</p>
          <button className="btn btn-primary" onClick={onUpload} style={{ marginTop: 12 }} data-testid="photos-empty-upload-btn">
            <Plus size={16} /> Ajouter une photo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="photos-tab-grouped">
      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 12px', background: 'white', border: '1px solid #E2E2E6', borderRadius: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ImageIcon size={18} color="#5E5F66" />
          <strong style={{ fontSize: '0.92rem' }}>{photos.length}</strong>
          <span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>photo{photos.length > 1 ? 's' : ''}</span>
          <span style={{ color: '#9E9E9E', fontSize: '0.78rem' }}>· {groups.length} dossier{groups.length > 1 ? 's' : ''}</span>
        </div>
        <input
          type="text"
          placeholder="Rechercher (commentaire, auteur, dossier)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="photos-search-input"
          style={{ flex: '1 1 220px', minWidth: 200, padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #E2E2E6', borderRadius: 6 }}
        />
        <div style={{ display: 'inline-flex', borderRadius: 6, border: '1px solid #E2E2E6', overflow: 'hidden' }} role="group" aria-label="Mode d'affichage">
          {[
            { id: 'carousel', label: 'Carrousel', icon: Rows },
            { id: 'grid',     label: 'Grille',    icon: GridFour },
            { id: 'flat',     label: 'Liste',     icon: ImageIcon },
          ].map(({ id, label, icon: I }) => (
            <button
              key={id}
              type="button"
              onClick={() => setViewMode(id)}
              data-testid={`photos-view-${id}`}
              style={{
                padding: '6px 10px',
                background: viewMode === id ? '#1D4ED8' : 'white',
                color: viewMode === id ? 'white' : '#5E5F66',
                border: 'none',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <I size={13} /> {label}
            </button>
          ))}
        </div>
        {!readOnly && (
          <button className="btn btn-primary btn-sm" onClick={onUpload} data-testid="photos-add-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Ajouter
          </button>
        )}
      </div>

      {/* Body */}
      {viewMode === 'flat' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {(search.trim()
            ? allPhotosFlat.filter(p => (p.comment || '').toLowerCase().includes(search.toLowerCase()) || (p.uploaded_by_name || '').toLowerCase().includes(search.toLowerCase()))
            : allPhotosFlat
          ).map(p => (
            <PhotoCard key={p.id} photo={p} {...photoProps} />
          ))}
        </div>
      ) : (
        <div>
          {filteredGroups.length === 0 ? (
            <div className="card" style={{ padding: 20, textAlign: 'center', color: '#5E5F66' }}>
              Aucun resultat pour « {search} »
            </div>
          ) : (
            filteredGroups.map((g, idx) => (
              <FolderAccordion
                key={g.key}
                group={g}
                defaultOpen={idx < 2 /* open first 2 by default */}
                viewMode={viewMode}
                {...photoProps}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PhotosTabGrouped;
